import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Search, User, Users, Home, Building2 } from 'lucide-react';
import { STATUS_COLORS, STATUS_LEGEND } from './calendarUtils';
import { SHIFT_PERIODS, ShiftPeriodBadge } from './ShiftPeriodBadge';

const GROUP_OPTIONS = [
  { value: 'participant', label: 'By Participant', icon: Users },
  { value: 'staff', label: 'By Staff', icon: User },
  { value: 'sil', label: 'By SIL House', icon: Home },
  { value: 'centre', label: 'By Centre', icon: Building2 },
];

// Left filters panel for Calendar V3: search, status, period, group-by, legend.
export function CalendarFilters({ searchTerm, setSearchTerm, statusFilter, setStatusFilter, periodFilter, setPeriodFilter, groupBy, setGroupBy, openRoster, setOpenRoster, branchFilter, setBranchFilter, branches = [],
  hideCancelled, setHideCancelled, hiddenCancelledCount = 0 }) {
  const togglePeriod = (p) => {
    const set = new Set(periodFilter || []);
    if (set.has(p)) set.delete(p); else set.add(p);
    setPeriodFilter(Array.from(set));
  };
  return (
    <div className="w-52 flex-shrink-0 border-r bg-slate-50/50 p-3 space-y-4 overflow-y-auto" data-testid="filters-panel">
      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-1 block">Search</Label>
        <div className="relative">
          <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-muted-foreground" />
          <Input placeholder="Staff, participant..." className="pl-7 h-8 text-xs" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="search-filter" />
        </div>
      </div>
      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-1 block">Status</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_LEGEND.map(({ status, label }) => <SelectItem key={status} value={status}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-1 block">Shift Period</Label>
        <div className="flex flex-wrap gap-1" data-testid="period-filter">
          {SHIFT_PERIODS.map(p => {
            const active = (periodFilter || []).includes(p);
            return (
              <button key={p} onClick={() => togglePeriod(p)}
                className={`focus:outline-none ${active ? 'opacity-100 ring-2 ring-offset-1 ring-slate-400 rounded-full' : 'opacity-60 hover:opacity-100'}`}
                data-testid={`period-filter-${p}`}>
                <ShiftPeriodBadge period={p} showIcon={false} />
              </button>
            );
          })}
        </div>
        {(periodFilter?.length || 0) > 0 && (
          <button onClick={() => setPeriodFilter([])} className="text-[10px] text-muted-foreground hover:text-foreground mt-1" data-testid="period-filter-clear">Clear</button>
        )}
      </div>
      <Separator />
      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-1 block">View</Label>
        <button onClick={() => setOpenRoster?.(!openRoster)}
          className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-colors ${openRoster ? 'bg-amber-500 text-white' : 'hover:bg-slate-100 text-slate-700'}`}
          data-testid="open-roster-toggle" aria-pressed={!!openRoster}>
          <span>Open Roster (unfilled)</span><span className="text-[10px]">{openRoster ? 'ON' : 'OFF'}</span>
        </button>
        {/* BS3-003: cancelled shifts that will not be invoiced (reason + history stay on the shift record) */}
        {setHideCancelled && (
          <>
            <button onClick={() => setHideCancelled(!hideCancelled)}
              className={`w-full flex items-center justify-between px-2 py-1.5 mt-1 rounded text-xs transition-colors ${hideCancelled ? 'bg-slate-700 text-white' : 'hover:bg-slate-100 text-slate-700'}`}
              title="Cancelled shifts that will not be invoiced. Billable cancellations stay visible; choose Status = Cancelled to see them all."
              data-testid="hide-cancelled-toggle" aria-pressed={!!hideCancelled}>
              <span>Hide cancelled</span><span className="text-[10px]">{hideCancelled ? 'ON' : 'OFF'}</span>
            </button>
            {hideCancelled && hiddenCancelledCount > 0 && (
              <p className="text-[10px] text-muted-foreground mt-0.5 px-1" data-testid="hidden-cancelled-count">
                {hiddenCancelledCount} cancelled shift{hiddenCancelledCount === 1 ? '' : 's'} hidden
              </p>
            )}
          </>
        )}
        {branches.length > 0 && (
          <Select value={branchFilter || 'all'} onValueChange={v => setBranchFilter?.(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs mt-2" data-testid="branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {branches.map(b => <SelectItem key={b.id} value={b.id} data-testid={`branch-filter-${b.id}`}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      <Separator />
      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-1 block">Group By</Label>
        <div className="space-y-1">
          {GROUP_OPTIONS.map(opt => (
            <button key={opt.value} onClick={() => setGroupBy(opt.value)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${groupBy === opt.value ? 'bg-slate-900 text-white' : 'hover:bg-slate-100 text-slate-700'}`}
              data-testid={`group-by-${opt.value}`}>
              <opt.icon className="w-3.5 h-3.5" /> {opt.label}
            </button>
          ))}
        </div>
      </div>
      <Separator />
      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-2 block">Legend</Label>
        <div className="space-y-1" data-testid="calendar-legend">
          {STATUS_LEGEND.map(({ status, label, hint }) => {
            const c = STATUS_COLORS[status];
            return (
              <div key={status} className="flex items-center gap-2 text-[11px]" data-testid={`legend-${status}`}>
                {/* a mini card in the same colours as the shift cards */}
                <span className={`w-5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 ${c.bg}`}><span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} /></span>
                <span className="font-medium text-slate-700">{label}</span>
                <span className="text-slate-400 truncate">· {hint}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
